import viewTransitionNavUtility from "./viewTransitionNavUtility";

export {
  PDP_EXPAND_EASING,
  PDP_EXPAND_TRANSITION_MS,
  PDP_FORWARD_TRANSITION_MS,
} from "./pdpViewTransitionConstants";

export type PdpViewTransitionDirection = "left-to-right" | "pdp-image-expand";

export function getPropertyImageViewTransitionName(inventoryId: string | number): string {
  return `property-image-${inventoryId}`;
}

export function getPropertyContentViewTransitionName(inventoryId: string | number): string {
  return `property-content-${inventoryId}`;
}

export type PdpSharedTransitionElements = {
  inventoryId: string | number;
  imageEl?: HTMLElement | null;
  contentEl?: HTMLElement | null;
};

/** Assign shared-element names on the clicked card only — not on every list card. */
export function applyPdpSharedViewTransitionNames({
  inventoryId,
  imageEl,
  contentEl,
}: PdpSharedTransitionElements): void {
  if (typeof document === "undefined") return;

  const imageName = getPropertyImageViewTransitionName(inventoryId);
  const contentName = getPropertyContentViewTransitionName(inventoryId);
  if (imageEl) imageEl.style.viewTransitionName = imageName;
  if (contentEl) contentEl.style.viewTransitionName = contentName;
}

/** Find image/content slots inside a card root via data attributes. */
export function applyPdpSharedViewTransitionNamesFromCard(
  inventoryId: string | number,
  cardRoot?: HTMLElement | null,
): void {
  if (!cardRoot) return;

  applyPdpSharedViewTransitionNames({
    inventoryId,
    imageEl: cardRoot.querySelector<HTMLElement>("[data-pdp-vt-image]"),
    contentEl: cardRoot.querySelector<HTMLElement>("[data-pdp-vt-content]"),
  });
}

export const APP_PROMOTION_BANNER_VIEW_TRANSITION_NAME = "app-promotion-banner";

/** True while PDP → search back transition is running (skip shared-element names). */
export function isPdpBackViewTransitionActive(): boolean {
  return typeof document !== "undefined" && document.body.classList.contains("pdp-back-vt");
}

/** Remove PDP shared-element names before back nav (release uses root slide only). */
export function stripPropertyImageViewTransitionNames(): void {
  if (typeof document === "undefined") return;

  document.querySelectorAll<HTMLElement>("*").forEach((el) => {
    const inlineName = el.style.viewTransitionName;
    const computedName =
      inlineName ||
      (typeof getComputedStyle !== "undefined" ? getComputedStyle(el).viewTransitionName : "");
    if (
      computedName &&
      computedName !== "none" &&
      (computedName.startsWith("property-image-") ||
        computedName.startsWith("property-content-") ||
        computedName === "pdp-header" ||
        computedName === APP_PROMOTION_BANNER_VIEW_TRANSITION_NAME)
    ) {
      el.style.viewTransitionName = "none";
    }
  });
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** Mobile/mweb only — desktop opens PDP in new tab or skips VT. */
export function isPdpViewTransitionSupported(): boolean {
  if (typeof document === "undefined") return false;
  if (typeof __DESKTOP__ !== "undefined" && __DESKTOP__) return false;
  if (prefersReducedMotion()) return false;
  return Boolean(document.startViewTransition);
}

/** Back navigation with the original slide-out animation (PDP → search). */
export function navigateBackWithPdpViewTransition(navigate: () => void): void {
  stripPropertyImageViewTransitionNames();
  document.body.classList.add("pdp-back-vt");

  requestAnimationFrame(() => {
    viewTransitionNavUtility(navigate, "left-to-right");
  });
}

/** Card image + content expand into PDP; page crossfades, no slide. */
export function navigateWithPdpViewTransition(
  navigate: () => void,
  direction: PdpViewTransitionDirection = "pdp-image-expand",
  sharedElements?: PdpSharedTransitionElements,
): void {
  if (!isPdpViewTransitionSupported()) {
    navigate();
    return;
  }

  if (sharedElements) {
    applyPdpSharedViewTransitionNames(sharedElements);
  }

  const mode = direction === "left-to-right" ? "left-to-right" : ("pdp-image-expand" as const);

  viewTransitionNavUtility(navigate, mode);
}
